import { Button, DatePicker } from "@devhop/ui";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type AttendanceHistoryFilterProps = {
  filters: FilterConditionDTO[];
  onFiltersChange: (filters: FilterConditionDTO[] | undefined) => void;
};

export function AttendanceHistoryFilter({
  filters,
  onFiltersChange,
}: AttendanceHistoryFilterProps) {
  const sessionTypeFilter = findCondition(filters, "sessionType");
  const checkedInAtFilter = findCondition(filters, "checkedInAt");
  const classTypeFilter = findCondition(filters, "classTypeId");
  const levelFilter = findCondition(filters, "levelId");

  const [sessionTypeValue, setSessionTypeValue] = useState<string>(
    (sessionTypeFilter?.value as string) || "all",
  );
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [classTypeId, setClassTypeId] = useState<string | null>(null);
  const [levelId, setLevelId] = useState<string | null>(null);

  useEffect(() => {
    setSessionTypeValue((sessionTypeFilter?.value as string) || "all");
  }, [sessionTypeFilter?.value]);

  // useEffect(() => {
  //   // Extract date range from filter
  //   if (
  //     checkedInAtFilter?.op === "and" &&
  //     Array.isArray(checkedInAtFilter.value)
  //   ) {
  //     const dateConditions = checkedInAtFilter.value as FilterConditionDTO[];
  //     const fromCondition = dateConditions.find((c) => c.op === "gte");
  //     const toCondition = dateConditions.find((c) => c.op === "lte");

  //     const fromDateStr =
  //       typeof fromCondition?.value === "string"
  //         ? fromCondition.value.slice(0, 10)
  //         : "";
  //     const toDateStr =
  //       typeof toCondition?.value === "string"
  //         ? toCondition.value.slice(0, 10)
  //         : "";

  //     setFromDate(fromDateStr ? new Date(fromDateStr) : undefined);
  //     setToDate(toDateStr ? new Date(toDateStr) : undefined);
  //   } else {
  //     setFromDate(undefined);
  //     setToDate(undefined);
  //   }
  // }, [checkedInAtFilter]);

  useEffect(() => {
    const classTypeIdValue =
      typeof classTypeFilter?.value === "string" ? classTypeFilter.value : null;
    setClassTypeId(classTypeIdValue);
  }, [classTypeFilter?.value]);

  useEffect(() => {
    const levelIdValue =
      typeof levelFilter?.value === "string" ? levelFilter.value : null;
    setLevelId(levelIdValue);
  }, [levelFilter?.value]);

  const handleSessionTypeChange = (val: string) => {
    setSessionTypeValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val === "all") {
      nextFilters = removeConditions(filters, "sessionType");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "sessionType",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[]
      | undefined,
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;

    setFromDate(from);
    setToDate(to);

    const fromStr = from ? format(from, "yyyy-MM-dd") : "";
    const toStr = to ? format(to, "yyyy-MM-dd") : "";

    let nextFilters: FilterConditionDTO[] | undefined = filters;
    nextFilters = removeConditions(nextFilters, "checkedInAt");

    if (fromStr || toStr) {
      const dateRange: FilterConditionDTO[] = [];
      if (fromStr) {
        dateRange.push({
          field: "checkedInAt",
          op: "gte",
          value: `${fromStr} 00:00:00`,
        });
      }
      if (toStr) {
        dateRange.push({
          field: "checkedInAt",
          op: "lte",
          value: `${toStr} 23:59:59`,
        });
      }

      if (dateRange.length > 0) {
        nextFilters = upsertCondition(nextFilters, {
          field: "checkedInAt",
          op: "and",
          value: dateRange as unknown as FilterConditionDTO["value"],
        });
      }
    }

    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleClassTypeChange = (val: string | null) => {
    setClassTypeId(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, "classTypeId");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "classTypeId",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleLevelChange = (val: string | null) => {
    setLevelId(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, "levelId");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "levelId",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const hasFilter =
    !!sessionTypeFilter?.value ||
    !!checkedInAtFilter ||
    !!classTypeFilter?.value ||
    !!levelFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <SimpleSelect
            value={sessionTypeValue}
            onValueChange={handleSessionTypeChange}
            placeholder="ປະເພດ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "general_class", label: "ຄລາດສ໌ທົ່ວໄປ" },
              { value: "private_lesson", label: "ຮຽນສ່ວນຕົວ" },
            ]}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate,
              to: toDate,
            }}
            onChange={handleDateRangeChange}
            placeholder="ຊ່ວງວັນທີເຂົ້າຮຽນ"
            className="w-full sm:w-fit"
          />

          {sessionTypeValue === "general_class" ||
          sessionTypeValue === "all" ? (
            <>
              <InfiniteCombobox<LookupItem>
                queryKey={["master", "class-types", "lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/master/class-types/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(
                    `${config.apiUrl}/master/class-types/lookup`,
                    id,
                  )
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={classTypeId || ""}
                onValueChange={(val) => handleClassTypeChange(val || null)}
                placeholder="ປະເພດຄລາດສ໌"
                className="h-8 w-full sm:w-48"
                clearable
              />

              <InfiniteCombobox<LookupItem>
                queryKey={["master", "levels", "lookup"]}
                queryFn={(args) =>
                  fetchLookupForInfinite(
                    `${config.apiUrl}/master/levels/lookup`,
                    args,
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem(`${config.apiUrl}/master/levels/lookup`, id)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                value={levelId || ""}
                onValueChange={(val) => handleLevelChange(val || null)}
                placeholder="ລະດັບ"
                className="h-8 w-full sm:w-48"
                clearable
              />
            </>
          ) : null}
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onFiltersChange(undefined);
              setToDate(undefined);
              setFromDate(undefined);
              setClassTypeId(null);
              setLevelId(null);
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
